import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import * as Yup from "yup";
import { baseURL } from "../../services/axios/eps";
import { toast } from "sonner";
import {
  createSection,
  getCategoriesForHP,
  updateSection,
} from "../../services/axios/apiManager";
import { SwitchButtonMedium } from "../../Components/switchButton";
import {
  handleErrors,
  formatDateForInput,
  getTodayDateString,
  getNextWeekDateString,
} from "../../services/dataManager";

const CategoryWiseForm = ({
  id,
  selectedOption,
  navigate,
  gamesList,
  sectionData,
}) => {
  const [loading, setLoading] = useState(false);
  const [timed, setTimed] = useState(1);
  const [selectedGames, setSelectedGames] = useState([]);
  const [gameQuery, setGameQuery] = useState("");
  const [gameCats, setGameCats] = useState([]);
  const [gamesByCategory, setGamesByCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);

  const parsedGamesList = useMemo(() => {
    if (!id || !sectionData?.games_list) return [];
    if (typeof sectionData.games_list === "string") {
      try {
        return JSON.parse(sectionData.games_list);
      } catch (e) {
        return [];
      }
    }
    return Array.isArray(sectionData.games_list) ? sectionData.games_list : [];
  }, [id, sectionData?.games_list]);

  const parsedMisc = useMemo(() => {
    if (!id || !sectionData?.misc) return null;
    if (typeof sectionData.misc === "string") {
      try {
        return JSON.parse(sectionData.misc);
      } catch (e) {
        return null;
      }
    }
    return sectionData.misc;
  }, [id, sectionData?.misc]);

  const formik = useFormik({
    initialValues: {
      heading: id ? (sectionData?.heading || "") : "",
      status: id ? (sectionData?.status !== undefined ? sectionData.status : 1) : 1,
      games_included: id ? (sectionData?.games_included !== undefined ? sectionData.games_included : 1) : 1,
      games_list: parsedGamesList,
      section_type: selectedOption || "",
      is_timed: id ? (sectionData?.is_timed !== undefined ? sectionData.is_timed : 1) : 1,
      misc: parsedMisc,
      start_date:
        id && sectionData?.start_date
          ? formatDateForInput(sectionData.start_date)
          : getTodayDateString(),
      end_date:
        id && sectionData?.end_date
          ? formatDateForInput(sectionData.end_date)
          : getNextWeekDateString(),
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      heading: Yup.string().required("category is required!"),
      start_date: Yup.date()
        .nullable()
        .when("is_timed", {
          is: (val) => val === 1 || val === true,
          then: () =>
            Yup.date()
              .required("Start date is required")
              .min(
                new Date(new Date().setHours(0, 0, 0, 0)),
                "Start date cannot be in the past",
              )
              .nullable(),
          otherwise: () => Yup.date().nullable(),
        }),
      end_date: Yup.date()
        .nullable()
        .when(["is_timed", "start_date"], {
          is: (is_timed, start_date) =>
            (is_timed === 1 || is_timed === true) && start_date,
          then: () =>
            Yup.date()
              .required("End date is required")
              .min(
                Yup.ref("start_date"),
                "End date cannot be before start date",
              )
              .nullable(),
          otherwise: () => Yup.date().nullable(),
        }),
      games_list: Yup.array()
        .required("Game list is required, select atleast 1 game")
        .min(1, "Select atleast 1 game"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const payload = {
          ...values,
          section_type: selectedOption,
        };
        console.log("payload :", payload);
        let res;
        if (id) {
          res = await updateSection({ id, ...payload });
        } else {
          res = await createSection(payload);
        }
        if (res?.data?.success) {
          toast.success(res?.data?.message || "Section saved successfully");
          navigate("/home-page");
        } else {
          toast.error(res?.data?.message || "Failed to save section");
        }
      } catch (err) {
        console.error(err);
        toast.error("An error occurred while saving");
      } finally {
        setLoading(false);
      }
    },
  });

  // // // Filter games list

  const filteredGames =
    gamesByCategory?.filter((game) => {
      if (!gameQuery) return true;
      const gameTitle = game?.title?.toLowerCase() || "";
      const searchQuery = gameQuery.toLowerCase();
      return gameTitle.includes(searchQuery);
    }) || [];

  // // // Add/Remove game from selection
  const handleSelectGame = useCallback(
    (id) => {
      setSelectedGames((prev) => {
        const newSelection = prev.includes(id)
          ? prev.filter((item) => item !== id)
          : [...prev, id];
        formik.setFieldValue("games_list", newSelection);
        return newSelection;
      });
    },
    [formik],
  );

  const handleChangeCategory = useCallback(
    (event) => {
      const cat = event.target.value;
      if (cat) {
        const catName = event.target.selectedOptions[0]?.text;
        formik.setFieldValue("heading", catName);
        formik.setFieldValue("misc", { category: cat });
        setSelectedCategory(parseInt(cat));
        const tempGames = gamesList.filter((f) => f.category === parseInt(cat));
        setGamesByCategory(tempGames);
        const tempGamesIds = tempGames.map((game) => game.id);
        setSelectedGames(tempGamesIds);
        formik.setFieldValue("games_list", tempGamesIds);
      } else {
        formik.setFieldValue("heading", "");
        formik.setFieldValue("misc", null);
        setSelectedCategory("");
        setSelectedGames([]);
        formik.setFieldValue("games_list", []);
        setGamesByCategory(null);
      }
    },
    [gamesList, formik],
  );

  const handleSwitchBtn = () => {
    if (timed === 1) {
      setTimed(0);
      formik.setFieldValue("is_timed", 0);
    } else {
      setTimed(1);
      formik.setFieldValue("is_timed", 1);
    }
  };

  const getCategories = async () => {
    const res = await getCategoriesForHP();
    if (res?.data?.success && res.data?.data) {
      setGameCats(res.data.data);
    } else {
      handleErrors(res, navigate, "Home Page -> Game Categories: ");
    }
  };

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      window.scroll({ top: 0, behavior: "smooth" });
    }
  }, [formik.submitCount, formik.errors]);

  useEffect(() => {
    setIsInitialized(false);
  }, [id, sectionData?.id]);

  useEffect(() => {
    if (!id) {
      setSelectedGames([]);
      setSelectedCategory("");
      setGamesByCategory(null);
      setIsInitialized(true);
      return;
    }

    if (isInitialized) return;
    if (!sectionData || Object.keys(sectionData).length === 0) return;
    if (!gamesList || gamesList.length === 0) return;

    let initialGames = [];
    if (sectionData?.games_list) {
      if (typeof sectionData.games_list === "string") {
        try {
          initialGames = JSON.parse(sectionData.games_list);
        } catch (e) {
          initialGames = [];
        }
      } else if (Array.isArray(sectionData.games_list)) {
        initialGames = sectionData.games_list;
      }
    }
    setSelectedGames(initialGames);

    let categoryId = null;
    if (sectionData?.misc) {
      let parsedMiscObj = null;
      if (typeof sectionData.misc === "string") {
        try {
          parsedMiscObj = JSON.parse(sectionData.misc);
        } catch (e) {}
      } else {
        parsedMiscObj = sectionData.misc;
      }
      if (parsedMiscObj && parsedMiscObj.category) {
        categoryId = parseInt(parsedMiscObj.category, 10);
      }
    }

    if (categoryId !== null && !isNaN(categoryId)) {
      setSelectedCategory(categoryId);
      const tempGames = gamesList.filter((f) => f.category === categoryId);
      setGamesByCategory(tempGames);
    } else {
      setSelectedCategory("");
      setGamesByCategory(null);
    }

    setIsInitialized(true);
  }, [id, sectionData, gamesList, isInitialized]);

  useEffect(() => {
    setTimed(formik.values.is_timed === 1 ? 1 : 0);
  }, [formik.values.is_timed]);

  useEffect(() => {
    getCategories();
  }, []);

  return (
    <div className="align-item-center bg-dark mt-5 rounded">
      <Form onSubmit={formik.handleSubmit} className="p-3 rounded">
        <Row className="mb-4">
          <Col className="sm-12 md-12">
            <Form.Label>Select Category</Form.Label>
            <Form.Select
              data-testid="heading"
              className="bg-dark text-light border-light input-placeholder-light"
              style={{ paddingTop: "9px", paddingBottom: "9px" }}
              value={selectedCategory}
              onChange={(e) => {
                handleChangeCategory(e);
              }}
              isInvalid={formik.touched.heading && !!formik.errors.heading}
            >
              <option value="">Select Category</option>
              {gameCats?.map((cat) => (
                <option value={cat?.id}>{cat.name}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {formik.errors.heading}
            </Form.Control.Feedback>
          </Col>
        </Row>
        <div className="container-fluid py-2 px-0 mb-3">
          <div className="d-flex align-items-center">
            <Form.Label className="mb-0 mx-2 text-light font-semibold">
              Timed Section
            </Form.Label>
            <SwitchButtonMedium
              isOn={formik.values.is_timed === 1}
              color={"#00af43ff"}
              onClick={() => {
                handleSwitchBtn();
              }}
            />
            <span
              className={`mx-3 text-sm font-medium ${formik.values.is_timed === 1 ? "text-success" : "text-muted"}`}
              style={{ fontSize: "0.9rem" }}
            >
              {formik.values.is_timed === 1
                ? "Yes (Active during specified dates)"
                : "No (Always Active)"}
            </span>
          </div>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateRows: timed === 1 ? "1fr" : "0fr",
            transition: "grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              minHeight: "0px",
              transition: "opacity 0.3s ease-in-out, padding 0.35s ease-in-out",
              opacity: timed === 1 ? 1 : 0,
              paddingBottom: timed === 1 ? "1.5rem" : "0px",
            }}
          >
            <Row className="mb-4">
              <Col xs={12} md={6}>
                <Form.Label>Start Date</Form.Label>
                <Form.Control
                  type="date"
                  data-testid="start-date"
                  className="text-light border-light bg-dark py-4 input-placeholder-light"
                  {...formik.getFieldProps("start_date")}
                  isInvalid={
                    formik.touched.start_date && !!formik.errors.start_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.start_date}
                </Form.Control.Feedback>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label>End Date</Form.Label>
                <Form.Control
                  type="date"
                  data-testid="end-date"
                  className="bg-dark text-light border-light input-placeholder-light py-4"
                  {...formik.getFieldProps("end_date")}
                  isInvalid={
                    formik.touched.end_date && !!formik.errors.end_date
                  }
                />
                <Form.Control.Feedback type="invalid">
                  {formik.errors.end_date}
                </Form.Control.Feedback>
              </Col>
            </Row>
          </div>
        </div>
        <hr />
        {gamesByCategory ? (
          <div>
            <p className="text-light font-bold">
              {`Select Games (${selectedGames?.length || 0} selected )`}
            </p>
            <span className="text-danger">{formik.errors.games_list}</span>
            <Form.Control
              type="text"
              name="game-search"
              placeholder="Search games..."
              data-testid="game-search"
              className="text-light bg-dark border-light py-4 input-placeholder-light"
              value={gameQuery}
              onChange={(e) => setGameQuery(e.target.value)}
            />
            <div className="flex bg-dark rounded mt-4">
              <div
                className="row w-100 rounded overflow-auto"
                style={{ minHeight: "100px", maxHeight: "600px" }}
              >
                {filteredGames?.length ? (
                  filteredGames.map((game) => (
                    <div
                      className="col-xs-12 col-sm-12 col-md-4"
                      key={game.id}
                      onClick={() => {
                        handleSelectGame(game.id);
                      }}
                    >
                      <div
                        className={`d-flex justify-content-between align-items-center rounded-lg cursor-pointer border-2 p-2 m-2 ${selectedGames?.includes(game.id) ? "border-2 border-indigo-600 bg-slate-800" : "border-2 bg-dark"}`}
                      >
                        <div className="d-flex align-items-center">
                          <img
                            src={`${baseURL}/uploads/file/${game?.icon}`}
                            crossOrigin="anonymous"
                            alt="game-icon"
                            style={{
                              height: "60px",
                              width: "60px",
                              borderRadius: "10px",
                              objectFit: "contain",
                            }}
                          />
                          <p className="font-bold ml-2">{game.title}</p>
                        </div>
                        {selectedGames?.includes(game.id) && (
                          <div className="text-indigo-600 dark:text-indigo-400">
                            <svg
                              className="w-5 h-5"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-light">No games to show</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center text-gray-500">
            <div className="text-center">
              <p>Select a category to view the games list</p>
            </div>
          </div>
        )}
        <hr />
        <div className="d-flex justify-content-end mt-4 gap-2">
          <Button
            variant="secondary"
            onClick={() => navigate("/home-page")}
            // disabled={loading}
          >
            Cancel
          </Button>
          <Button variant="success" type="submit" disabled={loading}>
            {loading ? "Saving..." : id ? "Save Changes" : "Create Section"}
          </Button>
        </div>
      </Form>
    </div>
  );
};
export default CategoryWiseForm;
